import { useIntl } from "react-intl";
import { DataTableNumberColumn } from "~components/DataTable/components/DataTableNumberColumn/DataTableNumberColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { useSeasonStageService } from "~pages/SeasonStages/hooks/useSeasonStageService.ts";
import { SeasonStageTableColumns } from "~pages/SeasonStages/SeasonStageListPage/enums/seasonStageTableColumns.ts";
import type { UpdateSeasonStageModel } from "~pages/SeasonStages/types/seasonStageModel.ts";
import type { Stage } from "~types/stage.ts";

export const useGetSeasonStageTableColumns = (massEditMode: boolean): Array<DataTableColumn<Stage>> => {
  const { formatMessage } = useIntl();
  const { updateSeasonStage } = useSeasonStageService();

  const updateSeasonStageField = async (row: Stage & DataTableRow, updateData: Omit<UpdateSeasonStageModel, "stageCode">) => {
    row.disabled = true;
    try {
      await updateSeasonStage(row.stageCode, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: SeasonStageTableColumns.NameLocal,
      title: formatMessage({ id: "name.local" }),
      dataIndex: "nameLocal",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateSeasonStageField(row, { nameLocal })}
        />
      ),
    },
    {
      key: SeasonStageTableColumns.AbbreviationLocal,
      title: formatMessage({ id: "name.local.abbreviation" }),
      dataIndex: "abbreviationLocal",
      width: 100,
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLocal) => updateSeasonStageField(row, { abbreviationLocal: abbreviationLocal || null })}
        />
      ),
    },
    {
      key: SeasonStageTableColumns.StageCode,
      title: formatMessage({ id: "stage.code" }),
      dataIndex: "stageCode",
      copyable: false,
      width: 240,
      sorter: true,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonStageTableColumns.StageOrder,
      title: formatMessage({ id: "stage.order" }),
      dataIndex: "stageOrder",
      width: 150,
      sorter: true,
      render: (value: number | null, row) => (
        <DataTableNumberColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(stageOrder) => updateSeasonStageField(row, { stageOrder })}
        />
      ),
    },
    {
      key: SeasonStageTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin" }),
      dataIndex: "nameLatin",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateSeasonStageField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: SeasonStageTableColumns.AbbreviationLatin,
      title: formatMessage({ id: "name.latin.abbreviation" }),
      dataIndex: "abbreviationLatin",
      width: 100,
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLatin) => updateSeasonStageField(row, { abbreviationLatin: abbreviationLatin || null })}
        />
      ),
    },
    {
      key: SeasonStageTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: false,
      width: 200,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(externalId) => updateSeasonStageField(row, { externalId: externalId || null })}
        />
      ),
    },
  ];
};
